import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AlertService } from '../../shared/services/alert.service';
import { OtpPurpose } from '../models/otp-purpose.enum';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly STORAGE_KEY = 'mt_is_logged_in';
  private readonly TOKEN_KEY = 'mt_auth_token';
  private readonly apiUrl = environment.apiUrl;
  private alertService = inject(AlertService);
  private platformId = inject(PLATFORM_ID);

  private isLoggedInSubject = new BehaviorSubject<boolean>(this.readPersistedState());
  public isLoggedIn$: Observable<boolean> = this.isLoggedInSubject.asObservable();

  private pendingRegisterData: any = null;

  constructor(private http: HttpClient) {}

  setPendingRegisterData(data: any): void {
    this.pendingRegisterData = data;
  }

  getPendingRegisterData(): any {
    return this.pendingRegisterData;
  }

  clearPendingRegisterData(): void {
    this.pendingRegisterData = null;
  }

  getAlertService(): AlertService {
    return this.alertService;
  }

  get isLoggedIn(): boolean {
    return this.isLoggedInSubject.value;
  }

  isAuthenticated(): boolean {
    return this.isLoggedIn;
  }

  getCurrentUser(): any {
    // Mock user for testing VIP flow
    if (this.isLoggedIn) {
      return { id: 'current-user-id', name: 'Test User' };
    }
    return null;
  }

  isListingOwner(listing: any): boolean {
    // For demo purposes, we can assume listing with ID '1' is owned by the current user
    if (!this.isLoggedIn) return false;
    const user = this.getCurrentUser();
    return user && listing.id === '1';
  }

  getToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch {
      return null;
    }
  }

  login(phone: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/Auth/Login`, { phone, password }).pipe(
      tap((res) => {
        if (res && res.success && res.data && res.data.accessToken) {
          this.persistToken(res.data.accessToken);
          this.isLoggedInSubject.next(true);
          this.persistState(true);
        } else if (res && res.accessToken) { // Ehtiyat variant
          this.persistToken(res.accessToken);
          this.isLoggedInSubject.next(true);
          this.persistState(true);
        } else if (res && res.data && res.data.token) { // Köhnə variant üçün ehtiyat
          this.persistToken(res.data.token);
          this.isLoggedInSubject.next(true);
          this.persistState(true);
        }
      })
    );
  }

  register(userData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/Auth/Register`, userData);
  }

  sendOtp(phone: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/Auth/send-otp`, { phone });
  }

  verifyOtp(phone: string, code: string, purpose: OtpPurpose): Observable<any> {
    return this.http.post(`${this.apiUrl}/Auth/verify-otp`, { phone, code, purpose });
  }

  forgotPassword(phone: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/Auth/forgot-password`, { phone });
  }

  resetPassword(data: { phone: string; otpCode: string; newPassword: string; confirmPassword: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}/Auth/reset-password`, data);
  }

  logout(): void {
    this.isLoggedInSubject.next(false);
    this.persistState(false);
    this.clearToken();
  }

  private normalizePhone(input: string): string {
    // Keep country code +994 and join groups as "+994 51 743 23 63" style for compare
    const digits = (input || '').replace(/\D/g, '');
    // Expect 12 digits with country code 994 + 9-digit local (994XXXXXXXXX)
    if (!digits.startsWith('994')) {
      return input?.trim() || '';
    }
    const rest = digits.slice(3); // 9 digits: XX XXX XX XX (we'll format to match)
    const a = rest.slice(0, 2);
    const b = rest.slice(2, 5);
    const c = rest.slice(5, 7);
    const d = rest.slice(7, 9);
    return `+994 ${a} ${b} ${c} ${d}`.trim();
  }

  private persistState(val: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      localStorage.setItem(this.STORAGE_KEY, val ? '1' : '0');
    } catch {}
  }

  private persistToken(token: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
    } catch {}
  }

  private clearToken(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      localStorage.removeItem(this.TOKEN_KEY);
    } catch {}
  }

  private readPersistedState(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    try {
      const hasToken = !!localStorage.getItem(this.TOKEN_KEY);
      const isLoggedIn = localStorage.getItem(this.STORAGE_KEY) === '1';
      return hasToken || isLoggedIn;
    } catch { return false; }
  }
}
